import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/* ============================================================
   EMERGÊNCIAS E SIMULACROS (SGI — ISO 45001, 8.2)
   Registo de simulacros + equipa de emergência (config).
   ============================================================ */

const TIPOS = { geral: 'Geral', parcial: 'Parcial' };
const RESULTADOS = {
    satisfatorio: { label: 'Satisfatório', bg: '#dcfce7', fg: '#166534' },
    a_melhorar: { label: 'A melhorar', bg: '#fef9c3', fg: '#854d0e' },
    insatisfatorio: { label: 'Insatisfatório', bg: '#fee2e2', fg: '#991b1b' },
};
const PLANEADO = { label: '🗓️ Planeado', bg: '#e0f2fe', fg: '#075985' };
const fmtData = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return '—'; } };

/* ---------- Simulacro (criar/editar) ---------- */
function SimulacroModal({ simulacro, onClose, onSaved }) {
    const novo = !simulacro;
    const campos = ['data', 'hora', 'tipo', 'local', 'cenario', 'objetivo', 'participantes', 'tempo_evacuacao', 'meios_utilizados', 'pontos_melhoria', 'resultado', 'estado', 'observacoes'];
    const [form, setForm] = useState(() => {
        const base = { tipo: 'geral', resultado: 'satisfatorio', estado: 'realizado' };
        campos.forEach(k => { base[k] = simulacro?.[k] ?? (base[k] || ''); });
        if (simulacro?.data) base.data = String(simulacro.data).substring(0, 10);
        return base;
    });
    const planeado = form.estado === 'planeado';
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const guardar = async () => {
        if (!form.data) { setErr('Indique a data.'); return; }
        setSaving(true); setErr('');
        try {
            if (novo) await api.post('/sistema-integrado/simulacros', form);
            else await api.put(`/sistema-integrado/simulacros/${simulacro.id}`, form);
            onSaved(); onClose();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao guardar'); }
        finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 680 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>{novo ? 'Novo simulacro' : `Simulacro ${fmtData(simulacro.data)}`}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <div data-liquid-grid="true" style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Estado</label>
                        <select value={form.estado} onChange={e => upd('estado', e.target.value)} style={{ ...inp, width: '100%' }}>
                            <option value="realizado">Realizado</option>
                            <option value="planeado">Planeado (agendado)</option>
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>{planeado ? 'Data prevista *' : 'Data *'}</label>
                        <input type="date" value={form.data} onChange={e => upd('data', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div data-liquid-grid="true" style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Hora</label>
                        <input value={form.hora} onChange={e => upd('hora', e.target.value)} placeholder="ex.: 10:30" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Tipo</label>
                        <select value={form.tipo} onChange={e => upd('tipo', e.target.value)} style={{ ...inp, width: '100%' }}>
                            {Object.entries(TIPOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Local</label>
                    <input value={form.local} onChange={e => upd('local', e.target.value)} style={{ ...inp, width: '100%' }} />
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Cenário</label>
                    <textarea value={form.cenario} onChange={e => upd('cenario', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Objetivo</label>
                    <textarea value={form.objetivo} onChange={e => upd('objetivo', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                {planeado ? (
                    <div data-liquid-notice="true" data-liquid-tone={liquidTone('#e0f2fe')} style={{ background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: 8, padding: '8px 12px', fontSize: '0.82rem', color: '#075985', marginBottom: 10 }}>
                        Simulacro <strong>agendado</strong>. Os participantes, tempo de evacuação e resultado preenchem-se depois de realizado (mude o estado para "Realizado").
                    </div>
                ) : (
                    <div data-liquid-grid="true" style={grid3}>
                        <div>
                            <label data-liquid-label="true" style={lbl}>Participantes</label>
                            <input type="number" min="0" value={form.participantes} onChange={e => upd('participantes', e.target.value)} style={{ ...inp, width: '100%' }} />
                        </div>
                        <div>
                            <label data-liquid-label="true" style={lbl}>Tempo de evacuação</label>
                            <input value={form.tempo_evacuacao} onChange={e => upd('tempo_evacuacao', e.target.value)} placeholder="ex.: 3 min" style={{ ...inp, width: '100%' }} />
                        </div>
                        <div>
                            <label data-liquid-label="true" style={lbl}>Resultado</label>
                            <select value={form.resultado} onChange={e => upd('resultado', e.target.value)} style={{ ...inp, width: '100%' }}>
                                {Object.entries(RESULTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select>
                        </div>
                    </div>
                )}
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Meios utilizados</label>
                    <textarea value={form.meios_utilizados} onChange={e => upd('meios_utilizados', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Pontos de melhoria (oportunidades identificadas)</label>
                    <textarea value={form.pontos_melhoria} onChange={e => upd('pontos_melhoria', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: 6 }}>
                    <label data-liquid-label="true" style={lbl}>Observações</label>
                    <textarea value={form.observacoes} onChange={e => upd('observacoes', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                {err && <div style={{ color: '#e53e3e', margin: '10px 0', fontSize: '0.85rem' }}>{err}</div>}
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'A guardar…' : 'Guardar'}</button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Equipa de emergência ---------- */
function EquipaModal({ onClose }) {
    const [membros, setMembros] = useState(null);
    const [novo, setNovo] = useState({ funcao: '', nome: '', contacto: '' });
    const [err, setErr] = useState('');

    const carregar = useCallback(async () => {
        try { const r = await api.get('/sistema-integrado/equipa-emergencia'); setMembros(r.data || []); }
        catch { setMembros([]); }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    const adicionar = async () => {
        if (!novo.funcao.trim()) { setErr('Indique a função.'); return; }
        setErr('');
        try { await api.post('/sistema-integrado/equipa-emergencia', novo); setNovo({ funcao: '', nome: '', contacto: '' }); carregar(); }
        catch (e) { setErr(e.response?.data?.error || 'Erro'); }
    };
    const atualizar = async (m, patch) => {
        try { await api.put(`/sistema-integrado/equipa-emergencia/${m.id}`, { ...m, ...patch }); carregar(); }
        catch { alert('Erro ao atualizar'); }
    };
    const remover = async (m) => {
        if (!window.confirm('Remover este membro?')) return;
        try { await api.delete(`/sistema-integrado/equipa-emergencia/${m.id}`); carregar(); }
        catch { alert('Erro ao remover'); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 620 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Equipa de emergência</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                {membros === null ? <p style={{ color: '#64748b' }}>A carregar…</p> : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem', marginBottom: 14 }}>
                        <thead>
                            <tr style={{ textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                                <th style={{ padding: '4px 6px' }}>Função</th>
                                <th style={{ padding: '4px 6px' }}>Nome</th>
                                <th style={{ padding: '4px 6px' }}>Contacto</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {membros.map(m => (
                                <tr key={m.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                                    <td style={{ padding: '4px 6px', fontWeight: 600 }}>{m.funcao}</td>
                                    <td style={{ padding: '4px 6px' }}>
                                        <input defaultValue={m.nome || ''} onBlur={e => e.target.value !== (m.nome || '') && atualizar(m, { nome: e.target.value })}
                                            placeholder="—" style={{ ...inp, width: '100%', padding: '3px 6px' }} />
                                    </td>
                                    <td style={{ padding: '4px 6px' }}>
                                        <input defaultValue={m.contacto || ''} onBlur={e => e.target.value !== (m.contacto || '') && atualizar(m, { contacto: e.target.value })}
                                            placeholder="—" style={{ ...inp, width: '100%', padding: '3px 6px' }} />
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <button data-liquid-secondary="true" onClick={() => remover(m)} style={{ ...btnSec, padding: '2px 8px', fontSize: '0.75rem', color: '#e53e3e' }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                    <div style={{ flex: 1, minWidth: 150 }}>
                        <label data-liquid-label="true" style={lbl}>Função</label>
                        <input value={novo.funcao} onChange={e => setNovo(p => ({ ...p, funcao: e.target.value }))} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 120 }}>
                        <label data-liquid-label="true" style={lbl}>Nome</label>
                        <input value={novo.nome} onChange={e => setNovo(p => ({ ...p, nome: e.target.value }))} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Contacto</label>
                        <input value={novo.contacto} onChange={e => setNovo(p => ({ ...p, contacto: e.target.value }))} style={{ ...inp, width: 110 }} />
                    </div>
                    <button data-liquid-primary="true" onClick={adicionar} style={btnPri}>+ Adicionar</button>
                </div>
                {err && <div style={{ color: '#e53e3e', marginTop: 8, fontSize: '0.85rem' }}>{err}</div>}
            </div>
        </LiquidDialog>
    );
}

/* ---------- Componente principal ---------- */
const Emergencia = () => {
    const [simulacros, setSimulacros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [equipa, setEquipa] = useState(false);
    const [expandido, setExpandido] = useState(null);

    const carregar = useCallback(async () => {
        setLoading(true);
        try { const r = await api.get('/sistema-integrado/simulacros'); setSimulacros(r.data || []); }
        catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    const eliminar = async (s) => {
        if (!window.confirm(`Eliminar o simulacro de ${fmtData(s.data)}?`)) return;
        try { await api.delete(`/sistema-integrado/simulacros/${s.id}`); carregar(); }
        catch { alert('Erro ao eliminar'); }
    };

    // O relatório registado pelo painel de Validades. A rota pede sessão, por
    // isso abre-se como ficheiro e não por ligação direta.
    const abrirRelatorio = async (s) => {
        try {
            const r = await api.get(s.relatorio_url.replace(/^\/api/, ''), { responseType: 'blob' });
            const url = window.URL.createObjectURL(r.data);
            window.open(url, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch { alert('Não foi possível abrir o relatório.'); }
    };

    // "Último" conta só simulacros já realizados (a lista vem ordenada por data desc)
    const ultimo = simulacros.find(s => s.estado !== 'planeado');
    const diasDesde = ultimo ? Math.floor((Date.now() - new Date(ultimo.data).getTime()) / 86400000) : null;
    const proximo = simulacros.filter(s => s.estado === 'planeado' && new Date(s.data) >= new Date()).slice(-1)[0];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <div style={{ color: '#64748b', fontSize: '0.9rem' }}>
                    {ultimo ? <>Último simulacro realizado há <strong>{diasDesde}</strong> dias ({fmtData(ultimo.data)}). Recomendado: pelo menos 1 por ano.</> : 'Sem simulacros realizados.'}
                    {proximo && <> · <span style={{ color: '#075985' }}>Próximo agendado para <strong>{fmtData(proximo.data)}</strong>.</span></>}
                </div>
                <div data-liquid-actions="true" style={{ display: 'flex', gap: 8 }}>
                    <button data-liquid-secondary="true" onClick={() => setEquipa(true)} style={btnSec}><LiquidText>👥 Equipa de emergência</LiquidText></button>
                    <button data-liquid-primary="true" onClick={() => setModal({})} style={btnPri}>+ Registar simulacro</button>
                </div>
            </div>

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : simulacros.length === 0 ? (
                    <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>Sem simulacros registados. Registe o primeiro.</div>
                ) : simulacros.map(s => {
                    const rs = s.estado === 'planeado' ? PLANEADO : (RESULTADOS[s.resultado] || RESULTADOS.satisfatorio);
                    const aberto = expandido === s.id;
                    return (
                        <div key={s.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
                            <div onClick={() => setExpandido(aberto ? null : s.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer', flexWrap: 'wrap' }}>
                                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}><LiquidText>{aberto ? '▼' : '▶'}</LiquidText></span>
                                <strong style={{ color: '#1e293b', minWidth: 96 }}>{fmtData(s.data)}{s.hora ? ` · ${s.hora}` : ''}</strong>
                                <span data-liquid-tone={liquidTone('#f1f5f9')} data-liquid-badge="true" style={{ ...badge, background: '#f1f5f9', color: '#475569' }}>{TIPOS[s.tipo] || s.tipo}</span>
                                <span style={{ flex: 1, color: '#334155', minWidth: 160 }}>{s.cenario || s.objetivo || '—'}</span>
                                <span data-liquid-tone={liquidTone(rs.bg)} data-liquid-badge="true" style={{ ...badge, background: rs.bg, color: rs.fg }}><LiquidText>{rs.label}</LiquidText></span>
                                <span onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                                    <button data-liquid-secondary="true" onClick={() => setModal({ simulacro: s })} style={{ ...btnSec, ...mini }}>Editar</button>
                                    <button data-liquid-secondary="true" onClick={() => eliminar(s)} style={{ ...btnSec, ...mini, color: '#e53e3e' }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                </span>
                            </div>
                            {aberto && (
                                <div style={{ borderTop: '1px solid #f1f5f9', padding: '12px 16px', fontSize: '0.86rem', color: '#475569', lineHeight: 1.5 }}>
                                    {s.local && <div><strong>Local:</strong> {s.local}</div>}
                                    {s.objetivo && <div><strong>Objetivo:</strong> {s.objetivo}</div>}
                                    {s.meios_utilizados && <div><strong>Meios:</strong> {s.meios_utilizados}</div>}
                                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', margin: '4px 0' }}>
                                        {s.participantes != null && <span><strong>Participantes:</strong> {s.participantes}</span>}
                                        {s.tempo_evacuacao && <span><strong>Tempo de evacuação:</strong> {s.tempo_evacuacao}</span>}
                                    </div>
                                    {s.pontos_melhoria && <div style={{ marginTop: 4 }}><strong>Pontos de melhoria:</strong> {s.pontos_melhoria}</div>}
                                    {s.observacoes && <div style={{ marginTop: 4, color: '#64748b' }}>{s.observacoes}</div>}
                                    {/* Só endereços da própria API: o pedido leva a sessão. */}
                                    {String(s.relatorio_url || '').startsWith('/api/') && (
                                        <div style={{ marginTop: 8 }}>
                                            <button data-liquid-secondary="true" onClick={() => abrirRelatorio(s)} style={{ ...btnSec, ...mini, marginLeft: 0 }}><LiquidText>📄 Relatório</LiquidText></button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

            {modal && <SimulacroModal simulacro={modal.simulacro} onClose={() => setModal(null)} onSaved={carregar} />}
            {equipa && <EquipaModal onClose={() => setEquipa(false)} />}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '4px 9px', marginLeft: 6, fontSize: '0.76rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };
const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginBottom: 12 };
const grid3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 };

export default Emergencia;
